import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  Text,
  Avatar,
  Flex,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import UpdateUserSelect, { SelectableUser } from "./UpdateUserSelect";
import {
  useApiMutation,
  useApiQuery,
} from "../../../../utilities/apibelRequest";
import useToast from "../../../../utilities/useToast";

type Props = {
  manualSectionResponsibleUser: {
    firstName?: string;
    lastName?: string;
    userID: string;
  };
  manualSectionID: string;
  isOpen: boolean;
  onClose: () => void;
};

export function EditManualSectionOwnerModal({
  manualSectionResponsibleUser,
  manualSectionID,
  isOpen,
  onClose,
}: Props) {
  const initialFocusRef = useRef(null);

  const toast = useToast();
  const queryClient = useQueryClient();

  const [selectedUser, setSelectedUser] = useState<SelectableUser | undefined>(
    undefined,
  );

  const userQuery = useApiQuery("user/selectable", null);
  const userData = userQuery.isSuccess ? userQuery.data : [];

  const updateSectionResponsibleUserMutation = useApiMutation(
    "manual/updateManualSectionResponsibleUser",
  );

  const isMutating = updateSectionResponsibleUserMutation.isLoading;

  const handleClose = () => {
    setSelectedUser(undefined);
    onClose();
  };

  const handleConfirmUpdateUser = async () => {
    // do the mutation, then close the modal

    try {
      if (!selectedUser) {
        return;
      }
      await updateSectionResponsibleUserMutation.mutateAsync({
        section: {
          manualSectionID,
          responsibleUserID: selectedUser.ID,
        },
      });
    } catch (e) {
      toast.displayToast({
        title: "Failed to update owner",
        status: "error",
      });
    }
    toast.displayToast({
      title: "Updated section owner",
      status: "success",
    });
    queryClient.invalidateQueries({queryKey: ["manual"]});

    handleClose();
  };

  return (
    <Modal
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={handleClose}>
      <ModalOverlay />
      <chakra-scope>
        <ModalContent>
          <ModalHeader>Edit Section Owner</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex>
              <VStack align="left">
                <VStack align="left" pb={4}>
                  <Text fontSize="sm" color="gray.500">
                    Current owner
                  </Text>
                  <Text>
                    <Avatar
                      size="xs"
                      name={`${manualSectionResponsibleUser.firstName} ${manualSectionResponsibleUser.lastName}`}
                    />{" "}
                    {`${manualSectionResponsibleUser.firstName} ${manualSectionResponsibleUser.lastName}`}
                  </Text>
                </VStack>
                <VStack align="left">
                  <Text fontSize="sm" color="gray.500">
                    Choose a new owner
                  </Text>
                  <UpdateUserSelect
                    users={userData}
                    selectedUser={selectedUser}
                    onChange={(value) => setSelectedUser(value)}
                  />
                </VStack>
              </VStack>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button onClick={handleClose} colorScheme="gray">
                Cancel
              </Button>
              <Button
                isLoading={isMutating}
                loadingText="Updating owner..."
                onClick={handleConfirmUpdateUser}>
                Update Owner
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </chakra-scope>
    </Modal>
  );
}
